// ============================================================
// ANSICHTSSACHE-AGENT – Zahlen auf Deutsch
// ============================================================
// Ein Betrag wird hier gelesen wie auf einer Rechnung: Komma vor
// den Cent, Punkt zwischen den Tausendern. Das galt bisher nur
// dort, wo `formatCurrency` stand — daneben zeigten die
// Eingabefelder „123.51“, also die Schreibweise der Maschine,
// und ein Feld mit dem Platzhalter „50.000“ nahm als Zahl
// fuenfzig entgegen.
//
// Deshalb steht hier BEIDES an einer Stelle: das Formatieren
// fuer die Anzeige und das Lesen dessen, was jemand tippt. Wer
// ein Feld baut, braucht keine eigene Regel mehr dafuer, was
// „1.234,56“, „1234,56“ oder „1234.56“ bedeuten soll.
//
// Beim Lesen wird bewusst grosszuegig ausgelegt — wer aus einer
// Tabelle kopiert, bringt Tausenderpunkte, geschuetzte
// Leerzeichen und ein Eurozeichen mit. Nur das Ergebnis ist
// streng: eine Zahl oder `null`.
//
// NICHT hierher gehoeren XRechnung, DATEV und die CSV-Ausgaben.
// Deren Schreibweise ist vorgeschrieben und hat mit Lesbarkeit
// nichts zu tun; sie bleibt, wo sie ist.
// ============================================================

export const LOCALE = "de-DE";

/** Entfernt ein Trennzeichen restlos — `split`/`join` statt einer Regex je Zeichen. */
function ohne(text: string, zeichen: string): string {
  return text.split(zeichen).join("");
}

/**
 * Steht das Zeichen mehrfach, kann es nur gruppieren — dann
 * muessen aber auch die Gruppen stimmen: „1.234.567“ ja,
 * „12,,“ oder „1,23,4“ nein. Sonst wuerde aus einem Vertipper
 * klammheimlich eine Zahl.
 */
function istGruppiert(digits: string, sep: string): boolean {
  const gruppen = digits.split(sep);
  if (gruppen[0].length < 1 || gruppen[0].length > 3) return false;
  return gruppen.slice(1).every((g) => /^\d{3}$/.test(g));
}

/**
 * Welches Zeichen trennt die Nachkommastellen, welches gruppiert?
 * Gibt die Ziffernfolge mit Punkt als Dezimalzeichen zurueck —
 * oder `null`, wenn die Trennzeichen keinen Sinn ergeben.
 */
function normalizeSeparators(digits: string): string | null {
  const comma = digits.lastIndexOf(",");
  const dot = digits.lastIndexOf(".");

  // Beide Zeichen vorhanden: das HINTERE trennt die
  // Nachkommastellen, das vordere gruppiert die Tausender.
  // „1.234,56“ und „1,234.56“ ergeben so beide 1234.56.
  if (comma >= 0 && dot >= 0) {
    const decimal = comma > dot ? "," : ".";
    const group = decimal === "," ? "." : ",";
    return ohne(digits, group).replace(decimal, ".");
  }

  // Nur Kommas: im Deutschen ist das Komma das Dezimalzeichen.
  // Mehrere Kommas kann nur meinen, wer englisch gruppiert.
  if (comma >= 0) {
    if (digits.split(",").length > 2) {
      return istGruppiert(digits, ",") ? ohne(digits, ",") : null;
    }
    return digits.replace(",", ".");
  }

  // Nur Punkte: mehrere sind eindeutig eine Gruppierung. Ein
  // einzelner mit genau drei Ziffern dahinter ebenfalls —
  // „1.500“ sind fuenfzehnhundert und nicht anderthalb.
  if (dot >= 0) {
    if (digits.split(".").length > 2) {
      return istGruppiert(digits, ".") ? ohne(digits, ".") : null;
    }
    if (digits.length - dot - 1 === 3) return ohne(digits, ".");
    return digits;
  }

  return digits;
}

/**
 * Liest, was jemand getippt oder eingefuegt hat.
 * `null` heisst „keine Zahl“ — und ist ausdruecklich nicht 0,
 * sonst wird aus einem leer gelassenen Feld ein Nullbetrag.
 */
export function parseDecimal(raw: string | number | null | undefined): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (raw === null || raw === undefined) return null;

  const text = String(raw)
    // Auch schmales und geschuetztes Leerzeichen — die bringt
    // jede Tabelle mit, aus der kopiert wird.
    .replace(/[\s  ]/g, "")
    .replace(/€|EUR/gi, "");
  if (!text) return null;

  const match = text.match(/^([+-]?)([\d.,]+)$/);
  if (!match) return null;

  const normalized = normalizeSeparators(match[2]);
  if (normalized === null) return null;

  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return match[1] === "-" ? -value : value;
}

/** Wie `parseDecimal`, aber in Cent — die Waehrung liegt im Datenmodell als Ganzzahl. */
export function parseAmountCents(raw: string | number | null | undefined): number | null {
  const value = parseDecimal(raw);
  return value === null ? null : Math.round(value * 100);
}

/**
 * Was ein Formularfeld an den Server gibt: Punkt als
 * Dezimalzeichen, keine Gruppierung. `parseFloat` und
 * `new Decimal()` auf der anderen Seite verstehen nur das.
 */
export function toCanonical(value: number | null): string {
  return value === null ? "" : String(value);
}

/** Zahl → deutsche Schreibweise. Leer bleibt leer, damit kein „NaN“ im Feld steht. */
export function formatDecimal(
  value: number | bigint | string | null | undefined,
  { min = 0, max = 2 }: { min?: number; max?: number } = {},
): string {
  const num =
    typeof value === "string"
      ? parseDecimal(value)
      : value === null || value === undefined
        ? null
        : Number(value);
  if (num === null || !Number.isFinite(num)) return "";
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: min,
    maximumFractionDigits: Math.max(min, max),
  }).format(num);
}

/** Cent → „1.234,56 €“ */
export function formatCurrency(cents: number | bigint, locale = LOCALE): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(Number(cents) / 100);
}

/**
 * Hundertstel → „249,00 $“ — ein Betrag in der Waehrung seines
 * Belegs (TK-0056). Gespeichert ist jede Waehrung in Hundertsteln,
 * auch der Yen; die Stellen setzt hier `Intl` nach der Waehrung.
 * Einen Code, den `Intl` nicht kennt, schreibt sie dahinter.
 */
export function formatMoney(cents: number | bigint, currency = "EUR", locale = LOCALE): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(
      Number(cents) / 100,
    );
  } catch {
    return `${formatAmount(cents)} ${currency}`;
  }
}

/** Ein Wechselkurs: „1,1403“ — vier Stellen mindestens, sechs hoechstens. */
export function formatKurs(kurs: number | string | null | undefined): string {
  return formatDecimal(kurs, { min: 4, max: 6 });
}

/** Cent → „1.234,56“ — derselbe Betrag ohne Zeichen, fuer Felder und Zahlenspalten. */
export function formatAmount(cents: number | bigint | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  return formatDecimal(Number(cents) / 100, { min: 2, max: 2 });
}

/** Menge: so viele Nachkommastellen wie noetig, hoechstens drei. „24“, „1,5“, „0,125“. */
export function formatQuantity(value: number | string | null | undefined): string {
  return formatDecimal(value, { min: 0, max: 3 });
}

/** Dezimalstunden mit einer festen Nachkommastelle: „7,5“. */
export function formatHoursDecimal(hours: number | string | null | undefined): string {
  return formatDecimal(hours, { min: 1, max: 1 });
}

/** Prozent, ganzzahlig gerundet: „87 %“ waere Sache des Aufrufers, hier steht nur die Zahl. */
export function formatPercent(value: number | string | null | undefined, max = 1): string {
  return formatDecimal(value, { min: 0, max });
}
